// frontend/src/components/UserManagement.js
import React, { useState, useEffect } from 'react';
import { UserPlus, Edit, Trash2, Key, Power, Check, X, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { buttonPrimary, buttonSecondary, buttonDanger, focusRing, swalConfirmColor, swalCancelColor } from '../theme';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    mustChangePassword: true
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.username) {
      toast.warning('Username is required');
      return;
    }

    if (!editingUser && !formData.password) {
      toast.warning('Password is required for new users');
      return;
    }

    if (!editingUser && formData.password.length < 8) {
      toast.warning('Password must be at least 8 characters');
      return;
    }

    try {
      setLoading(true);
      if (editingUser) {
        await axios.put(`/api/users/${editingUser.id}`, {
          username: formData.username,
          email: formData.email,
          role: formData.role,
          isActive: true
        });
        toast.success('User updated successfully!');
      } else {
        await axios.post('/api/users', formData);
        toast.success('User created successfully!');
      }
      await fetchUsers();
      resetForm();
    } catch (err) {
      console.error('Error saving user:', err);
      toast.error(err.response?.data?.message || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email || '',
      password: '',
      role: user.role,
      mustChangePassword: false
    });
    setShowForm(true);
  };

  const handleDelete = async (user) => {
    const result = await Swal.fire({
      title: 'Delete User?',
      text: `This will permanently delete ${user.username} and all their data`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalConfirmColor,
      cancelButtonColor: swalCancelColor,
      confirmButtonText: 'Yes, delete user!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        popup: 'rounded-lg',
        confirmButton: 'px-6 py-3 rounded-md font-medium',
        cancelButton: 'px-6 py-3 rounded-md font-medium'
      }
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await axios.delete(`/api/users/${user.id}`);
        await fetchUsers();
        toast.success('User deleted successfully!');
      } catch (err) {
        console.error('Error deleting user:', err);
        toast.error(err.response?.data?.message || 'Failed to delete user');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleResetPassword = async (user) => {
    const { value: formValues } = await Swal.fire({
      title: `Reset Password for ${user.username}`,
      html: `
        <div style="text-align: left;">
          <label style="display: block; margin-bottom: 8px; font-weight: 500;">New Password</label>
          <input id="swal-password" class="swal2-input" type="password" placeholder="Minimum 8 characters" style="margin: 0 0 16px 0; width: 100%;">
          <div style="margin-top: 12px;">
            <input type="checkbox" id="swal-force-change" style="margin-right: 8px;">
            <label for="swal-force-change" style="font-size: 14px;">Force user to change password on next login</label>
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonColor: swalConfirmColor,
      confirmButtonText: 'Reset Password',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const password = document.getElementById('swal-password').value;
        const forceChange = document.getElementById('swal-force-change').checked;
        if (!password) {
          Swal.showValidationMessage('Please enter a password');
          return false;
        }
        if (password.length < 8) {
          Swal.showValidationMessage('Password must be at least 8 characters');
          return false;
        }
        return { password, forceChange };
      }
    });

    if (formValues) {
      try {
        setLoading(true);
        await axios.post(`/api/users/${user.id}/reset-password`, {
          newPassword: formValues.password,
          forceChange: formValues.forceChange
        });
        toast.success(`Password reset for ${user.username}!`);
        if (formValues.forceChange) {
          toast.info('User must change password on next login');
        }
        await fetchUsers();
      } catch (err) {
        console.error('Error resetting password:', err);
        toast.error(err.response?.data?.message || 'Failed to reset password');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleActive = async (user) => {
    const action = user.is_active ? 'deactivate' : 'activate';
    const result = await Swal.fire({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} User?`,
      text: `This will ${action} ${user.username}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: swalConfirmColor,
      confirmButtonText: `Yes, ${action}`,
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await axios.patch(`/api/users/${user.id}/toggle-active`);
        await fetchUsers();
        toast.success(`User ${action}d successfully!`);
      } catch (err) {
        console.error('Error toggling user status:', err);
        toast.error(err.response?.data?.message || `Failed to ${action} user`);
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'user',
      mustChangePassword: true
    });
    setEditingUser(null);
    setShowForm(false);
    setShowPassword(false);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">User Management</h1>
        <p className="text-gray-600">Create and manage user accounts</p>
      </div>

      <div className="mb-6">
        <button
          onClick={() => setShowForm(!showForm)}
          className={`flex items-center gap-2 px-4 py-2 rounded-md ${buttonPrimary}`}
        >
          <UserPlus size={18} />
          Add New User
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">
            {editingUser ? 'Edit User' : 'Add New User'}
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
                  placeholder="Enter username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
                  placeholder="user@restaurant.local"
                />
              </div>

              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
                      placeholder="Minimum 8 characters"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-2.5 text-gray-500"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            {!editingUser && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="mustChangePassword"
                  checked={formData.mustChangePassword}
                  onChange={(e) => setFormData({...formData, mustChangePassword: e.target.checked})}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                />
                <label htmlFor="mustChangePassword" className="ml-2 text-sm text-gray-700">
                  Force user to change password on first login
                </label>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`px-6 py-2 rounded-md font-medium transition-colors disabled:bg-gray-400 ${buttonPrimary}`}
              >
                {loading ? 'Saving...' : (editingUser ? 'Update' : 'Create') + ' User'}
              </button>
              <button
                onClick={resetForm}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${buttonSecondary}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading && users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No users yet. Click "Add New User" to get started!
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Username</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Last Login</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {user.username}
                      {user.must_change_password && (
                        <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                          Must change password
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{user.email || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(user)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="Edit user"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleResetPassword(user)}
                          className="text-orange-600 hover:text-orange-800 transition-colors"
                          title="Reset password"
                        >
                          <Key size={18} />
                        </button>
                        <button
                          onClick={() => handleToggleActive(user)}
                          className={`transition-colors ${
                            user.is_active ? 'text-yellow-600 hover:text-yellow-800' : 'text-green-600 hover:text-green-800'
                          }`}
                          title={user.is_active ? 'Deactivate user' : 'Activate user'}
                        >
                          <Power size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          title="Delete user"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;